// src/components/OrderRequirements/api/orderRequirementsApi.js
// ============================================================================
// BLOCK ORAPI.0 — Order Requirements API client
// ----------------------------------------------------------------------------
// Purpose:
//  - Fetch customer list grouped by pending vs none
//  - Fetch queue rows for a selected customer
//  - Submit ledger payload to backend
//
// Conventions:
//  - Prefer camelCase in UI objects
//  - Accept snake_case OR camelCase from backend (adapter layer)
// ============================================================================

import axios from '../../../axiosClient.js';
import {
  buildLegacyRowsForInsert,
  submitLegacyInsertLocal,
} from '../../OrderEntry/api/orderEntryApi.js';

// ============================================================================
// BLOCK ORAPI.UTIL.1 — Small helpers
// ----------------------------------------------------------------------------
// asArray: defensive array casting
// trimStr: safe string conversion + trim
// ============================================================================
const asArray = (v) => (Array.isArray(v) ? v : []);
const trimStr = (v) => String(v ?? '').trim();
const normalizeSubmitMasid = (v) => trimStr(v).replace(/^(?:00|01)-/, '');
const compareShipToCodes = (a, b) => {
  const left = trimStr(a).toUpperCase();
  const right = trimStr(b).toUpperCase();

  if (!left && !right) return 0;
  if (!left) return 1;
  if (!right) return -1;
  if (left === '1') return -1;
  if (right === '1') return 1;

  const leftNumeric = /^\d+$/.test(left);
  const rightNumeric = /^\d+$/.test(right);
  if (leftNumeric && rightNumeric) return Number(left) - Number(right);
  if (leftNumeric !== rightNumeric) return leftNumeric ? -1 : 1;

  return left.localeCompare(right);
};

const mapShipToAddress = (r) => ({
  facilityAddress: trimStr(r?.['facility_address'] ?? r?.['facilityAddress']),
  shipToCity: trimStr(r?.['ship_to_city'] ?? r?.['shipToCity']),
  shipToState: trimStr(r?.['ship_to_state'] ?? r?.['shipToState']),
  shipToZip: trimStr(r?.['ship_to_zip'] ?? r?.['shipToZip']),
  shipToCode: trimStr(r?.['ship_to_code'] ?? r?.['shipToCode']),
});

function stripLegacyCommentPoRows(rows) {
  const blockedItems = new Set(['/C', '/C01', '/C22']);
  return asArray(rows).filter((row) => {
    const item = trimStr(row?.item).toUpperCase();
    return !blockedItems.has(item);
  });
}

function getErrorMessage(error, fallback) {
  return (
    trimStr(error?.detail?.message) ||
    trimStr(error?.response?.data?.message) ||
    trimStr(error?.message) ||
    fallback
  );
}

// ============================================================================
// BLOCK ORAPI.CUST.1 — Fetch grouped customers
// ----------------------------------------------------------------------------
// Endpoint: GET /api/order-requirements/customers
// Expected backend response:
//  {
//    pending_customers: [ ... ],
//    no_pending_customers: [ ... ]
//  }
//
// Output (UI-friendly):
//  {
//    pendingCustomers: [ { customerNo, customerName, facilityAddress, shipToCity, ... } ],
//    noPendingCustomers: [ ... ]
//  }
// ============================================================================
export async function fetchOrderRequirementCustomers() {
  const { data } = await axios.get('/api/order-requirements/customers');

  // --------------------------------------------------------------------------
  // BLOCK ORAPI.CUST.MAP.1 — Normalize a customer record
  // ----------------------------------------------------------------------------
  // Supports snake_case or camelCase depending on environment.
  // --------------------------------------------------------------------------
  const mapCustomer = (r) => {
    const rawShipTos = asArray(r?.['ship_to_addresses'] ?? r?.['shipToAddresses']);
    const shipToAddresses = rawShipTos
      .map(mapShipToAddress)
      .filter((entry) => entry.shipToCode && entry.shipToCode.toUpperCase() !== 'P')
      .sort((a, b) => compareShipToCodes(a.shipToCode, b.shipToCode));

    return {
      customerNo: trimStr(r['customer_no'] ?? r['customerNo']),
      customerName: trimStr(r['customer_name'] ?? r['customerName']),
      facilityAddress: trimStr(r['facility_address'] ?? r['facilityAddress']),
      shipToCity: trimStr(r['ship_to_city'] ?? r['shipToCity']),
      shipToState: trimStr(r['ship_to_state'] ?? r['shipToState']),
      shipToZip: trimStr(r['ship_to_zip'] ?? r['shipToZip']),
      shipToCode: trimStr(r['ship_to_code'] ?? r['shipToCode']),
      shipToAddresses: shipToAddresses.map((entry) => ({
        facilityAddress: entry.facilityAddress,
        shipToCity: entry.shipToCity,
        shipToState: entry.shipToState,
        shipToZip: entry.shipToZip,
        shipToCode: entry.shipToCode,
      })),
    };
  };

  const pending = asArray(data?.['pending_customers']).map(mapCustomer);
  const none = asArray(data?.['no_pending_customers']).map(mapCustomer);

  return { pendingCustomers: pending, noPendingCustomers: none };
}

// ============================================================================
// BLOCK ORAPI.QUEUE.1 — Fetch queue rows for a customer
// ----------------------------------------------------------------------------
// Endpoint: GET /api/order-requirements/queue?customer_no=...
//
// Notes:
//  - Backend returns snake_case by default
//  - We also return camelCase buckets (needToOrder/watchList) to keep flexibility
// ============================================================================
export async function fetchOrderRequirementQueue(customerNo) {
  const { data } = await axios.get('/api/order-requirements/queue', {
    params: { customer_no: customerNo },
  });

  // --------------------------------------------------------------------------
  // BLOCK ORAPI.QUEUE.SHAPE.1 — Normalize buckets
  // ----------------------------------------------------------------------------
  // Accept either style from backend:
  //  - need_to_order / watch_list
  //  - needToOrder / watchList
  // --------------------------------------------------------------------------
  const need = asArray(data?.['need_to_order'] ?? data?.['needToOrder']);
  const watch = asArray(data?.['watch_list'] ?? data?.['watchList']);

  return {
    // base identity fields (snake_case)
    customer_no: trimStr(data?.['customer_no']),
    customer_name: trimStr(data?.['customer_name']),
    facility_address: trimStr(data?.['facility_address']),

    // snake_case buckets (CustomerAccordion expects these today)
    need_to_order: need,
    watch_list: watch,

    // camelCase buckets (future-proofing / alternate consumers)
    needToOrder: need,
    watchList: watch,
  };
}

// ============================================================================
// BLOCK ORAPI.SUBMIT.1 — Submit ledger
// ----------------------------------------------------------------------------
// Endpoint: POST /api/order-requirements/submit-ledger
//
// Input uses camelCase (UI), payload uses snake_case (API).
// ============================================================================
export async function submitOrderRequirementLedger({
  company,
  customerNo,
  customerName,
  facilityAddress,
  shipToCode,
  notes,
  items,
}) {
  // --------------------------------------------------------------------------
  // BLOCK ORAPI.SUBMIT.PAYLOAD.1 — Payload builder
  // ----------------------------------------------------------------------------
  // Keep this as the single "source of truth" for request shape.
  // --------------------------------------------------------------------------
  const payload = {
    company,
    customer_no: normalizeSubmitMasid(customerNo),
    customer_name: customerName,
    facility_address: facilityAddress,
    ship_to_code: trimStr(shipToCode),
    notes,
    items,
  };

  const { data } = await axios.post('/api/order-requirements/submit-ledger', payload);

  // --------------------------------------------------------------------------
  // BLOCK ORAPI.SUBMIT.RESP.1 — Normalize response
  // --------------------------------------------------------------------------
  return {
    ok: Boolean(data?.ok),
    batchId: trimStr(data?.['batch_id']),
    submittedCount: Number(data?.['submitted_count'] ?? 0),
    derivedPoNumber: trimStr(data?.['derived_po_number']),
    raw: data,
  };
}

// ============================================================================
// BLOCK ORAPI.SUBMIT.2 — Submit to Order Entry pipeline
// ----------------------------------------------------------------------------
// Steps:
//   1) Build legacy rows via /api/order-entry/submit-order
//   2) Post rows to legacy insert endpoint
// Throws with `error.step` for targeted UI messaging:
//   - 'build'
//   - 'legacy-insert'
// ============================================================================
export async function submitOrderRequirementToOrderEntry(payload) {
  let built;
  const sanitizedPayload = {
    ...payload,
    customerNo: normalizeSubmitMasid(payload?.customerNo),
  };

  try {
    built = await buildLegacyRowsForInsert(sanitizedPayload);
  } catch (error) {
    const wrapped = new Error(getErrorMessage(error, 'Order Entry build failed.'));
    wrapped.step = 'build';
    wrapped.cause = error;
    throw wrapped;
  }

  if (!built?.ok || !Array.isArray(built?.legacyRows) || built.legacyRows.length === 0) {
    const wrapped = new Error('Order Entry build failed: no legacy rows returned.');
    wrapped.step = 'build';
    wrapped.detail = built?.raw;
    throw wrapped;
  }

  try {
    const filteredLegacyRows = stripLegacyCommentPoRows(built.legacyRows);

    if (filteredLegacyRows.length === 0) {
      const wrapped = new Error(
        'Order Entry build failed: no legacy rows remained after Order Requirements filtering.'
      );
      wrapped.step = 'build';
      wrapped.detail = built?.raw;
      throw wrapped;
    }

    const insertResponse = await submitLegacyInsertLocal(filteredLegacyRows);
    return {
      ok: true,
      legacyRowCount: filteredLegacyRows.length,
      insertResponse,
    };
  } catch (error) {
    const wrapped = new Error(getErrorMessage(error, 'Legacy insert failed.'));
    wrapped.step = trimStr(error?.step) || 'legacy-insert';
    wrapped.cause = error;
    throw wrapped;
  }
}
